/*
 Copyright (c) 2025. BrightNight. All rights reserved.
 Unauthorized copying of this file, via any medium is strictly prohibited.
 Proprietary and confidential.
 */

import type {
  FlowRunStatus,
  ListResultsQuery,
  SimulationProgressResponse,
} from '@poweralpha/common';
import {
  type InfiniteData,
  keepPreviousData,
  type UseMutationOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import type { HTTPError } from 'ky';
import { sum } from 'lodash';
import { api, getRunsStatus } from '../api';
import { isFlowRunning, PAGE_SIZE } from './const';
import { isRunActive } from './progress/progress';
import { simulationRunKeys } from './queryKeys';

export const useGetScenarioIdMutation = (
  opts?: UseMutationOptions<Array<{ scenario_id: string }>, HTTPError, Array<string>>,
) =>
  useMutation<Array<{ scenario_id: string }>, HTTPError, Array<string>>({
    ...opts,
    mutationFn: async (runIds) => api.get(`results/run-ids?runId=${runIds.join('&runId=')}`).json(),
  });

export const useSimulationInfiniteQuery = () =>
  useInfiniteQuery<
    ListResultsQuery<FlowRunStatus>,
    HTTPError,
    InfiniteData<ListResultsQuery<FlowRunStatus>> & { numRunning: number },
    ReturnType<typeof simulationRunKeys.list>,
    number
  >({
    queryKey: simulationRunKeys.list(),
    queryFn: ({ pageParam = PAGE_SIZE }) =>
      getRunsStatus({
        queryByUser: true,
        limit: PAGE_SIZE,
        offset: pageParam - PAGE_SIZE,
      }),
    select: (data) => {
      const reversedPages: Array<ListResultsQuery<FlowRunStatus>> = [];
      data.pages.forEach((page) => {
        const reversedPage = [...page.rows];
        reversedPage.reverse();
        reversedPages.push({ ...page, rows: reversedPage });
      });
      reversedPages.reverse();
      return {
        ...data,
        pages: reversedPages,
        numRunning: sum(data.pages.flatMap((res) => res.rows.filter(isFlowRunning).length)),
      };
    },
    initialPageParam: PAGE_SIZE,
    getNextPageParam: (_, p) => sum(p.flatMap((x) => x.rows.length)) + PAGE_SIZE,
    refetchInterval: ({ state }) =>
      sum(state.data?.pages.flatMap((res) => res.rows.filter(isFlowRunning).length)) > 0
        ? 5e3
        : false,
    placeholderData: keepPreviousData,
  });

const ACTIVE_REFETCH_MS = 5e3;
/** Slow poll while idle, so runs started elsewhere (e.g. by the agent) still show up. */
const IDLE_REFETCH_MS = 30e3;

/** The user's latest runs with progress for the running ones. */
export const useSimulationProgressQuery = () =>
  useQuery<SimulationProgressResponse, HTTPError>({
    queryKey: simulationRunKeys.progress(),
    queryFn: () => api.get('simulations/progress').json(),
    refetchInterval: ({ state }) =>
      // Same active states as the pill, so cancelling and paused runs keep the fast poll.
      state.data?.runs.some(isRunActive) ? ACTIVE_REFETCH_MS : IDLE_REFETCH_MS,
    // Background status for the whole session: a Prefect outage must not toast on every poll.
    meta: { suppressErrorToast: true },
  });
